"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { Locale } from "@/i18n/config";
import { ISO639_1_ORDERED } from "@/lib/data/languages-iso";

interface LanguageSelectProps {
  value: string;
  onChange: (code: string) => void;
  locale: Locale;
  id?: string;
  placeholder?: string;
}

function localeTag(locale: Locale): string {
  switch (locale) {
    case "ko": return "ko";
    case "en": return "en";
    case "ja": return "ja";
    case "zh": return "zh-Hans";
    case "zh-tw": return "zh-Hant";
    default: return "en";
  }
}

function displayLanguageName(code: string, locale: Locale): string {
  try {
    const dn = new Intl.DisplayNames([localeTag(locale)], { type: "language" });
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

function nativeLanguageName(code: string): string {
  try {
    const dn = new Intl.DisplayNames([code], { type: "language" });
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

export function LanguageSelect({
  value,
  onChange,
  locale,
  id,
  placeholder,
}: LanguageSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const currentLocalName = value ? displayLanguageName(value, locale) : "";
  const currentNative = value ? nativeLanguageName(value) : "";
  const selectedLabel = value
    ? currentLocalName === currentNative
      ? `${currentLocalName} (${value.toUpperCase()})`
      : `${currentLocalName} (${currentNative}) · ${value.toUpperCase()}`
    : "";
  const selectedCode = value ? value.toUpperCase() : "";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ISO639_1_ORDERED;

    return ISO639_1_ORDERED.filter((code) => {
      const locName = displayLanguageName(code, locale).toLowerCase();
      const natName = nativeLanguageName(code).toLowerCase();
      return (
        code.toLowerCase().includes(q) ||
        locName.includes(q) ||
        natName.includes(q)
      );
    });
  }, [query, locale]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const handleSelect = (code: string) => {
    onChange(code);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          readOnly={!open}
          value={open ? query : selectedLabel}
          placeholder={
            open
              ? placeholder ?? "Search language"
              : placeholder ?? displayLanguageName(locale, locale)
          }
          onFocus={() => {
            setOpen(true);
            setQuery("");
            requestAnimationFrame(() => inputRef.current?.select());
          }}
          onChange={(e) => {
            if (!open) setOpen(true);
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              setQuery("");
              (e.target as HTMLInputElement).blur();
            }
          }}
          className="field-input pr-10 cursor-pointer focus:cursor-text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-faint">
          {selectedCode && !open ? (
            <span className="text-xs tracking-widest">{selectedCode}</span>
          ) : (
            <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden>
              <path
                d="M1 1l5 5 5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              />
            </svg>
          )}
        </span>
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-auto rounded-md border border-line bg-paper shadow-[0_20px_60px_-20px_rgba(35,33,29,0.35)]">
          {filtered.length === 0 ? (
            <p className="px-4 py-3 text-sm text-faint">No results</p>
          ) : (
            <ul id={listboxId} role="listbox" className="py-1">
              {filtered.map((code) => {
                const locName = displayLanguageName(code, locale);
                const natName = nativeLanguageName(code);
                const active = code === value;
                const display =
                  locName === natName ? locName : `${locName} (${natName})`;

                return (
                  <li key={code} role="option" aria-selected={active}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect(code);
                      }}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-canvas ${
                        active ? "bg-mist text-strong" : "text-body"
                      }`}
                    >
                      <span className="flex-1 truncate">{display}</span>
                      <span className="text-xs tracking-widest text-faint uppercase">
                        {code}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function languageDisplayName(code: string, locale: Locale): string {
  const locName = displayLanguageName(code, locale);
  const natName = nativeLanguageName(code);
  return locName === natName ? locName : `${locName} (${natName})`;
}
